import type {
  ContentEntry,
  ContentKind,
  ContentWarning,
  ParsedContentFile,
  RangeBound,
  TableEntry,
  TableRow,
} from "./types";

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;
const OBSIDIAN_COMMENT_RE = /%%[\s\S]*?%%/g;
const ENTRY_HEADING_RE = /^##[ \t]+(.+?)[ \t]*#*[ \t]*$/;
const DICE_HEADER_RE = /^\d*d\d+(?:\s*[+-]\s*\d+)?$/i;
const LIST_ITEM_RE = /^\s*(?:[-*+]|\d+[.)])\s+(.*)$/;

/** Lookup form of a name: "Creature Type" -> "creature-type". */
export function slugify(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[\s_/]+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Parse one markdown file from a content folder. Every `## heading` defines an
 * entry; text before the first one (a `# title`, notes) is ignored.
 *
 * `frontmatter` may be passed when the caller already has it parsed (Obsidian's
 * metadataCache); otherwise the file's own frontmatter block is read.
 */
export function parseContentFile(
  text: string,
  kind: ContentKind,
  source: string,
  frontmatter?: Record<string, unknown>,
): ParsedContentFile {
  const warnings: ContentWarning[] = [];
  const fmMatch = text.match(FRONTMATTER_RE);
  const meta = frontmatter ?? (fmMatch ? parseFrontmatter(fmMatch[1]) : {});
  const body = (fmMatch ? text.slice(fmMatch[0].length) : text).replace(OBSIDIAN_COMMENT_RE, "");
  const setting = typeof meta.setting === "string" && meta.setting.trim() ? slugify(meta.setting) : undefined;

  const entries: ContentEntry[] = [];
  const sections = splitSections(body);
  for (const { label, lines } of sections) {
    const name = slugify(label);
    if (!name) {
      warnings.push({ source, message: `Heading "${label}" has no usable name` });
      continue;
    }
    const base = { name, label, setting, meta, source };
    if (kind === "generator") {
      const genBody = trimBlankLines(lines).join("\n");
      if (!genBody) {
        warnings.push({ source, entry: name, message: "Generator is empty" });
        continue;
      }
      entries.push({ ...base, kind: "generator", body: genBody });
      continue;
    }
    const table = parseTableSection(lines, kind, base, warnings);
    if (table) entries.push(table);
  }
  return { entries, warnings };
}

function splitSections(body: string): { label: string; lines: string[] }[] {
  const sections: { label: string; lines: string[] }[] = [];
  let inFence = false;
  for (const line of body.split(/\r?\n/)) {
    // A `## ` inside a fenced code block is content, not a new entry.
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence;
    const heading = inFence ? null : line.match(ENTRY_HEADING_RE);
    if (heading) {
      sections.push({ label: heading[1], lines: [] });
    } else if (sections.length > 0) {
      sections[sections.length - 1].lines.push(line);
    }
  }
  return sections;
}

function trimBlankLines(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && lines[start].trim() === "") start++;
  while (end > start && lines[end - 1].trim() === "") end--;
  return lines.slice(start, end);
}

function parseTableSection(
  lines: string[],
  kind: "table" | "resolution",
  base: Omit<TableEntry, "kind" | "formula" | "columns" | "rows" | "description">,
  warnings: ContentWarning[],
): TableEntry | null {
  const warn = (message: string) => warnings.push({ source: base.source, entry: base.name, message });

  const tableStart = lines.findIndex((l) => l.trim().startsWith("|"));
  const listStart = lines.findIndex((l) => LIST_ITEM_RE.test(l));
  const useTable = tableStart !== -1 && (listStart === -1 || tableStart < listStart);
  const start = useTable ? tableStart : listStart;
  if (start === -1) {
    warn("No table or list found under the heading");
    return null;
  }
  const description = trimBlankLines(lines.slice(0, start)).join("\n");

  const parsed = useTable ? parseMarkdownTable(lines, start, warn) : parseList(lines, start);
  if (!parsed || parsed.rows.length === 0) {
    warn("Table has no rows");
    return null;
  }
  return { ...base, kind, description, ...parsed };
}

function parseList(lines: string[], start: number): Pick<TableEntry, "formula" | "columns" | "rows"> {
  const rows: TableRow[] = [];
  for (let i = start; i < lines.length; i++) {
    const m = lines[i].match(LIST_ITEM_RE);
    if (!m) {
      if (lines[i].trim() === "") continue;
      break;
    }
    const n = rows.length + 1;
    rows.push({ min: num(n), max: num(n), cells: { result: m[1].trim() } });
  }
  return { formula: `1d${rows.length}`, columns: ["result"], rows };
}

function parseMarkdownTable(
  lines: string[],
  start: number,
  warn: (message: string) => void,
): Pick<TableEntry, "formula" | "columns" | "rows"> | null {
  const header = splitRow(lines[start]).map(stripEmphasis);
  if (header.length === 0) return null;

  const hasRollColumn = DICE_HEADER_RE.test(header[0].replace(/\s+/g, ""));
  const valueHeaders = hasRollColumn ? header.slice(1) : header;
  if (valueHeaders.length === 0) {
    warn("Table has a roll column but no value columns");
    return null;
  }
  const columns = uniqueColumnIds(valueHeaders);

  let i = start + 1;
  if (i < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i]) && lines[i].includes("-")) i++;

  const rows: TableRow[] = [];
  for (; i < lines.length && lines[i].trim().startsWith("|"); i++) {
    const cells = splitRow(lines[i]);
    let values = cells;
    let min: RangeBound;
    let max: RangeBound | null;
    if (hasRollColumn) {
      const range = parseRange(stripEmphasis(cells[0] ?? ""));
      if (!range) {
        warn(`Row ${rows.length + 1}: can't read roll "${cells[0] ?? ""}"`);
        continue;
      }
      ({ min, max } = range);
      values = cells.slice(1);
    } else {
      const n = rows.length + 1;
      min = num(n);
      max = num(n);
    }
    const row: Record<string, string> = {};
    columns.forEach((id, c) => {
      // A table row is one line, so multi-line cells are written with <br>.
      row[id] = (values[c] ?? "").trim().replace(/<br\s*\/?>/gi, "\n");
    });
    row.result = row[columns[0]];
    rows.push({ min, max, cells: row });
  }

  const formula = hasRollColumn ? header[0].replace(/\s+/g, "").toLowerCase() : `1d${rows.length}`;
  return { formula, columns, rows };
}

/** Split a markdown table row on unescaped pipes. `\|` becomes a literal pipe. */
function splitRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (s[i] === "|") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += s[i];
    }
  }
  cells.push(cur.trim());
  return cells;
}

function stripEmphasis(cell: string): string {
  return cell.replace(/\*\*|__|\*/g, "").trim();
}

function uniqueColumnIds(headers: string[]): string[] {
  const seen = new Map<string, number>();
  return headers.map((h, i) => {
    const id = slugify(h) || `column-${i + 1}`;
    const count = seen.get(id) ?? 0;
    seen.set(id, count + 1);
    return count === 0 ? id : `${id}-${count + 1}`;
  });
}

const num = (value: number): RangeBound => ({ kind: "num", value });

const BOUND = String.raw`\d+|[a-z_]\w*(?:\s*[+-]\s*\d+)?`;
const RANGE_RE = new RegExp(String.raw`^(${BOUND})\s*(?:(?:[-–—]|\bto\b)\s*(${BOUND}))?\s*(\+)?$`, "i");

/**
 * Read a roll cell: `4`, `1-2`, `1–2`, `25+`, `dc`, `dc-5–dc-1`, `1–dc-6`, `dc+5+`.
 * Variable names are lowercased so `DC-6` and `dc-6` are the same.
 */
export function parseRange(cell: string): { min: RangeBound; max: RangeBound | null } | null {
  const m = cell.trim().match(RANGE_RE);
  if (!m) return null;
  const min = parseBound(m[1]);
  if (m[3]) return { min, max: null };
  return { min, max: m[2] ? parseBound(m[2]) : min };
}

function parseBound(text: string): RangeBound {
  const t = text.replace(/\s+/g, "");
  if (/^\d+$/.test(t)) return num(parseInt(t, 10));
  const m = t.match(/^([a-z_]\w*)(?:([+-])(\d+))?$/i)!;
  const offset = m[2] ? (m[2] === "-" ? -1 : 1) * parseInt(m[3], 10) : 0;
  return { kind: "var", name: m[1].toLowerCase(), offset };
}

/**
 * Minimal frontmatter reader for when Obsidian's metadataCache is not available
 * (web app, docs build, tests). Handles `key: value` scalars and `[a, b]` lists,
 * which is all content files use.
 */
export function parseFrontmatter(block: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const line of block.split(/\r?\n/)) {
    const m = line.match(/^([A-Za-z_][\w-]*)\s*:\s*(.*?)\s*$/);
    if (!m) continue;
    out[m[1]] = parseScalar(m[2]);
  }
  return out;
}

function parseScalar(raw: string): unknown {
  const v = raw.replace(/\s+#.*$/, "");
  if (v === "") return null;
  if (/^\[.*\]$/.test(v)) {
    return v
      .slice(1, -1)
      .split(",")
      .map((s) => parseScalar(s.trim()))
      .filter((s) => s !== null);
  }
  if (/^(['"]).*\1$/.test(v)) return v.slice(1, -1);
  if (v === "true") return true;
  if (v === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(v)) return Number(v);
  return v;
}
